import React, { useContext, useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { Button } from 'antd'
import {
  useI18n,
  useRehabQuestion2,
  useRehabSelectedThemes,
  useUserProfile,
} from 'contexts/AuthContext'
import ContentHeader from 'views/Care/ContentHeader'
import { ScreenKey } from 'views/Care/util'
import mildPain from './img/ic-mild-pain.svg'
import discomfortingPain from './img/ic-discomforting-pain.svg'
import moderatePain from './img/ic-moderate-pain.svg'
import severePain from './img/ic-severe-pain.svg'
import unbelievablePain from './img/ic-unbelievable-pain.svg'
import classes from './index.module.scss'
import PainItem from 'components/PainItem/index'
import _ from 'lodash'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { useSkuCondition } from 'hooks/useSkuCondiction'
import { updateUserPromise } from 'apis/FitOnCareAPI'
import { useUserProfileStatus } from 'hooks/useUserProfileStatus'
import { getLocalLanguage } from 'utils/authLocalStorage'
import { AuthContext } from 'contexts/AuthContext'
const { styles } = classes

const question2Options = [
  {
    id: 1,
    name: 'Mild pain',
    image: mildPain,
  },
  {
    id: 2,
    name: 'Discomforting pain',
    image: discomfortingPain,
  },
  {
    id: 3,
    name: 'Moderate pain',
    image: moderatePain,
  },
  {
    id: 4,
    name: 'Severe pain',
    image: severePain,
  },
  {
    id: 5,
    name: 'Unbearable pain',
    image: unbelievablePain,
  },
]

const RehabQuestion2 = (props) => {
  const { sku, autoLoginSuccess } = props
  const { i18n } = useI18n()
  const { setRehabQuestion2, rehabQuestion2 } = useRehabQuestion2()
  const { rehabSelectedThemes } = useRehabSelectedThemes()
  const { getNewSkuCondition } = useSkuCondition()
  const { skuCondition, userProfile } = useUserProfile()
  const { getUserProfileStatus } = useUserProfileStatus()
  const authContext = useContext(AuthContext)
  const { theme } = useMemo(() => {
    return {
      theme: rehabSelectedThemes?.map((theme) => theme.name).join(','),
    }
  }, [rehabSelectedThemes])

  useEffect(() => {
    logAmplitudeEvent('Screen View: Onboarding - Recent Pain Level', {
      sku,
      theme,
      language: getLocalLanguage(),
    })
  }, [sku, theme])

  const onSubmit = async (item) => {
    setRehabQuestion2(item)
    logAmplitudeEvent('Click: Onboarding - Recent Pain Level', {
      sku,
      theme,
      level: item.id,
      language: getLocalLanguage(),
    })
  }

  const updateUserInfo = async () => {
    const themeIds = rehabSelectedThemes?.map((theme) => theme.id)
    const newSkuCondition = getNewSkuCondition({
      skuCondition,
      themeIds,
      sku,
    })
    updateUserPromise(
      {
        skuCondition: newSkuCondition,
        ...(rehabQuestion2?.id < 5 &&
          userProfile.skuStatus?.[sku] === 5 && {
            skuStatus: {
              sku,
              status: 1,
            },
          }),
      },
      authContext
    )
  }

  const onNext = async () => {
    switch (sku) {
      case 'pt':
        await updateUserInfo()
        const { shouldSkipFlow } = await getUserProfileStatus()
        if (shouldSkipFlow) {
          if (rehabQuestion2?.id < 5) {
            props.setCurrentKey(ScreenKey.SCHEDULED_TIME)
          } else {
            props.setCurrentKey(ScreenKey.REHAB_CONSULT_PROVIDER)
          }
        } else {
          if (rehabQuestion2?.id < 5) {
            props.setCurrentKey(ScreenKey.TALK_ABOUT_COVERAGE)
          } else {
            props.setCurrentKey(ScreenKey.REHAB_CONSULT_PROVIDER)
          }
        }
        break
      case 'msk':
        if (autoLoginSuccess) {
          await updateUserInfo()
        }
        props.setCurrentKey(ScreenKey.REHAB_CONSULT_PROVIDER)
        break
      default:
        props.setCurrentKey(ScreenKey.REHAB_CONSULT_PROVIDER)
        break
    }
  }

  return (
    <div className={classNames(styles, 'info-sec')}>
      <ContentHeader {...props} />
      <div className='info-content'>
        <div className='title'>
          {i18n(`How intense is your pain or discomfort today?`)}
        </div>
        <div className='pain-items'>
          {question2Options.map((item, index) => (
            <PainItem
              key={index}
              pain={item}
              selected={rehabQuestion2?.id === item.id}
              onClick={() => onSubmit(item)}
            />
          ))}
        </div>
        {!_.isEmpty(rehabQuestion2) && (
          <div className='pain-name'>{`${rehabQuestion2.id}-${i18n(
            rehabQuestion2.name
          )}`}</div>
        )}

        <div className='bottom-btn'>
          <Button
            className='btn-primary full-width-btn next-btn'
            disabled={_.isEmpty(rehabQuestion2)}
            onClick={() => {
              onNext()
            }}
          >
            {i18n('Next')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default RehabQuestion2
